/**
 * @engine/vfx — WGSL source scanning utilities for the node-function codegen.
 *
 * The codegen never rewrites author-written WGSL; it only *reads* it to decide
 * what a node function needs (environment builtins, library helpers, callable
 * gradient/curve ports). These helpers strip comments and collect identifiers.
 */

/** Strip line comments and block comments from WGSL source. */
export function stripComments(source: string): string {
  return source.replace(/\/\*[\s\S]*?\*\//g, ' ').replace(/\/\/[^\n]*/g, '');
}

/**
 * Collect every identifier appearing in WGSL source (comments excluded).
 * WGSL has no string literals, so a plain token scan is exact.
 */
export function collectIdentifiers(source: string): Set<string> {
  const clean = stripComments(source);
  const ids = new Set<string>();
  for (const m of clean.matchAll(/\b[A-Za-z_][A-Za-z0-9_]*\b/g)) {
    ids.add(m[0]);
  }
  return ids;
}

/**
 * Test whether `name` is *called* as a function somewhere in the source
 * (identifier directly followed by `(`). Used for callable gradient/curve ports.
 */
export function isCalled(source: string, name: string): boolean {
  const clean = stripComments(source);
  return new RegExp(`\\b${escapeRegExp(name)}\\s*\\(`).test(clean);
}

/**
 * Replace every *call* of `name(` with `replacement(`.
 * This is the one documented rewrite of the authoring contract: callable
 * gradient/curve ports (`gradient(t)`) are bound to their baked sampler fns.
 */
export function replaceCall(source: string, name: string, replacement: string): string {
  return source.replace(
    new RegExp(`\\b${escapeRegExp(name)}(\\s*\\()`, 'g'),
    `${replacement}$1`
  );
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * cyrb53-derived 64-bit content hash, hex-encoded (16 chars).
 * Deterministic across platforms; used to key specialized node functions
 * and baked gradient/curve samplers.
 */
export function contentHash(source: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < source.length; i++) {
    const ch = source.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0');
}

/** Sanitize an arbitrary id (node type / instance id) into a WGSL identifier fragment. */
export function sanitizeIdent(id: string): string {
  let result = id.replace(/[^a-zA-Z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  if (/^[0-9]/.test(result)) result = 'n' + result;
  return result || 'anon';
}
